import moment from "moment";

import { EcoTokenStatus, TIME_DISPLAY_UNIT, onReassignment, onVerification } from "./constants";
import { formatValue } from "./formatValue";
import { LandToken } from "../types/LandToken";

// ─── Public-facing labels (5 states only) ────────────────────────────────────
// "Pending" covers the V9.1 gap between the auction clock running out and the
// settlement reaching "finalised" — the winner holds no EFT yet, so the land is
// neither live nor Active.
export type PublicStatusLabel = "Idle" | "Offer" | "Auction" | "Pending" | "Active" | "Expired";

// ─── Internal labels for guardian / trust / admin roles ───────────────────────
type InternalStatusLabel =
  | "Blocked"
  | "Cancelled"
  | "Listed"
  | "Active"
  | "Mint Available"
  | "Expired"
  | "Verified"
  | "Verification"
  | "Claimed"
  | "Required"
  | "Reassignment"
  | "Rejected"
  | "Uploaded"
  | "Draft"
  | "Approved"
  | "Unverified"
  | "Pending";

export type StandardStatusLabel = PublicStatusLabel | InternalStatusLabel;

export type StandardStatusRole = "public" | "guardian" | "trust" | "admin";

export type StandardStatusResult = {
  label: StandardStatusLabel;
  color: string;
  showPulse: boolean;
  pulseWarning?: boolean;
  pulseSold?: boolean;
};

type StandardStatusInput = Pick<LandToken, "status" | "etNft" | "offerStatus" | "meta">;

type StandardStatusOptions = {
  role?: StandardStatusRole;
  lastVerificationStatus?: EcoTokenStatus | null;
  hasTrust?: boolean;
  forcePending?: boolean;
  approvedLabel?: "Approved" | "Verified";
};

// The auction has ended and the sale is being settled but is not yet final.
// True from the moment the clock runs out — the API still reports `started` for
// ~90s until the settle workers flip it to `completed` and then `ended` (see
// project_auction_settle_polling) — through every settlementState short of
// "finalised". An upheld deed challenge resets settlementState to "none" and
// hands the EFT back to the guardian, so it falls out of Pending on its own.
// `completed` carries no outcome (winner is still null) so it is always Pending;
// a no-bid `ended` has no settlement and falls through to Idle.
const isSettlementPending = (etNft: StandardStatusInput["etNft"]): boolean => {
  const auction = etNft?.etAuction;
  // settlementState is an et_nft column only — EtAuction's SettlementFields typing has no
  // backing column, so there is nothing to fall back to on the auction.
  const settlementState = etNft?.settlementState ?? null;

  if (settlementState && settlementState !== "none" && settlementState !== "finalised") {
    return true;
  }

  if (auction?.status === "completed") {
    return true;
  }

  return auction?.status === "started" && !!auction.auctionEnd && moment(auction.auctionEnd).isSameOrBefore(moment());
};

const asNumber = (value: unknown): number => {
  if (value === undefined || value === null) {
    return 0;
  }

  const formatted = formatValue(value as any, false);
  return Number(formatted || 0);
};

export const resolveStandardStatus = (
  { status, etNft, offerStatus, meta }: StandardStatusInput,
  {
    role = "public",
    lastVerificationStatus,
    hasTrust = false,
    forcePending = false,
    approvedLabel,
  }: StandardStatusOptions = {}
): StandardStatusResult | null => {
  // ─── PUBLIC ROLE — only 5 states exposed ──────────────────────────────────
  if (role === "public") {
    // LIVE — token is in an active offer or auction.
    if (offerStatus === "opened") {
      return { label: "Offer", color: "action.pulse", showPulse: true, pulseSold: true };
    }

    // PENDING — checked before Auction so an expired-but-still-`started` auction
    // stops reading as live, and before Active so the mid-settlement owner change
    // (an upheld deed challenge has to *revert* ownerAddress to the guardian, so it
    // moves before finalise) doesn't read as sold.
    if (isSettlementPending(etNft)) {
      return { label: "Pending", color: "warning.main", showPulse: true, pulseWarning: true };
    }

    if (etNft?.etAuction?.status === "started" || etNft?.status === "auction") {
      return { label: "Auction", color: "action.pulse", showPulse: true, pulseSold: false };
    }

    // ACTIVE / EXPIRED — token has been sold and the easement contract is running.
    const sold = !!etNft?.ownerAddress && !!etNft?.minterAddress && etNft.ownerAddress !== etNft.minterAddress;

    const contractStart = etNft?.easementActivated?.contractStart;

    if (sold && !!contractStart) {
      const contractEnd = moment(contractStart).add(Number(meta?.contractPeriod || 0), TIME_DISPLAY_UNIT);
      const hasTimeRemaining = contractEnd.isAfter(moment());

      const treeTotal = asNumber(etNft?.easementActivated?.treeTotal ?? etNft?.totalTreeForEasement);
      const rewardClaimed = asNumber(etNft?.easementActivated?.rewardClaimed);
      const remainingMint = treeTotal - rewardClaimed;

      // EXPIRED — no TR3 left AND time has elapsed.
      if (remainingMint <= 0 && !hasTimeRemaining) {
        return { label: "Expired", color: "text.primary", showPulse: false };
      }

      // ACTIVE — TR3 still available (regardless of time) or time still running.
      return { label: "Active", color: "action.pulse", showPulse: true, pulseSold: true };
    }

    // RE_VERIFICATION_REQUIRED still shows as Active to the public.
    const currentVerificationStatus = lastVerificationStatus ?? status;
    if (currentVerificationStatus === EcoTokenStatus.RE_VERIFICATION_REQUIRED) {
      return { label: "Active", color: "action.pulse", showPulse: true, pulseSold: false };
    }

    // IDLE — everything else (verified, blocked, cancelled, pre-mint pipeline).
    return { label: "Idle", color: "text.primary", showPulse: false };
  }

  // ─── INTERNAL ROLES (guardian / trust / admin) — full granular labels ─────

  if (status === EcoTokenStatus.BLOCKED) {
    return { label: "Blocked", color: "warning.main", showPulse: false };
  }

  if (status === EcoTokenStatus.INVALIDATED) {
    return { label: "Cancelled", color: "warning.main", showPulse: false };
  }

  if (offerStatus === "opened") {
    return { label: "Listed", color: "action.pulse", showPulse: true, pulseSold: true };
  }

  if (etNft?.etAuction?.status === "started" || etNft?.status === "auction") {
    return { label: "Listed", color: "action.pulse", showPulse: true, pulseSold: false };
  }

  const currentVerificationStatus = lastVerificationStatus ?? status;

  // Used in guardian and public profile tables for tokens requiring re-verification.
  if (currentVerificationStatus === EcoTokenStatus.RE_VERIFICATION_REQUIRED) {
    if (role === "trust" || role === "admin") {
      return { label: "Required", color: "warning.main", showPulse: true, pulseWarning: true };
    }

    return { label: "Active", color: "action.pulse", showPulse: true, pulseSold: false };
  }

  const sold = !!etNft?.ownerAddress && !!etNft?.minterAddress && etNft.ownerAddress !== etNft.minterAddress;

  const contractStart = etNft?.easementActivated?.contractStart;
  const hasContractStart = !!contractStart;

  if (sold && hasContractStart) {
    const contractEnd = moment(contractStart).add(Number(meta?.contractPeriod || 0), TIME_DISPLAY_UNIT);
    const hasTimeRemaining = contractEnd.isAfter(moment());

    const treeTotal = asNumber(etNft?.easementActivated?.treeTotal ?? etNft?.totalTreeForEasement);
    const rewardClaimed = asNumber(etNft?.easementActivated?.rewardClaimed);
    const remainingMint = treeTotal - rewardClaimed;

    if (remainingMint > 0 && hasTimeRemaining) {
      return { label: "Active", color: "action.pulse", showPulse: true, pulseSold: true };
    }

    if (remainingMint > 0 && !hasTimeRemaining) {
      return { label: "Active", color: "action.pulse", showPulse: true, pulseSold: true };
    }

    if (remainingMint <= 0 && !hasTimeRemaining) {
      return { label: "Expired", color: "text.primary", showPulse: false };
    }
  }

  if (status === EcoTokenStatus.VERIFIED && etNft?.id !== undefined && etNft?.id !== null) {
    return { label: "Verified", color: "text.primary", showPulse: false };
  }

  const inReassignment = onReassignment(currentVerificationStatus) || onReassignment(status);
  const inVerification = onVerification(currentVerificationStatus) || onVerification(status);

  // Used by unassigned trust lands where a row can be intentionally forced to pending.
  if (forcePending) {
    return { label: "Pending", color: "success.main", showPulse: true };
  }

  // Used in trust verification tables and admin reassignment workflows.
  if (inReassignment) {
    return { label: "Reassignment", color: "warning.main", showPulse: false };
  }

  // Used in trust dashboards where a token is already claimed by a verifier.
  if (currentVerificationStatus === EcoTokenStatus.CLAIMED) {
    return { label: "Claimed", color: "action.pulse", showPulse: true, pulseSold: true };
  }

  // Used in guardian/admin upload queues before trust claim starts.
  if (status === EcoTokenStatus.UPLOADED) {
    return { label: "Uploaded", color: "text.primary", showPulse: false };
  }

  // Used in trust/admin/guardian pending verification states.
  if (status === EcoTokenStatus.VERIFICATION_REQUIRED) {
    if (role === "trust" && !hasTrust) {
      return { label: "Verification", color: "action.pulse", showPulse: true, pulseSold: true };
    }

    if (role === "trust") {
      return { label: "Verification", color: "action.pulse", showPulse: true, pulseSold: true };
    }

    if (role === "guardian" || role === "admin") {
      return { label: "Uploaded", color: "text.primary", showPulse: false };
    }

    return { label: "Verification", color: "text.primary", showPulse: true, pulseWarning: true };
  }

  // Used in trust verification tables for ongoing verification states.
  if (inVerification) {
    return { label: "Verification", color: "text.primary", showPulse: true, pulseWarning: true };
  }

  // Used in guardian draft and trust-rejected draft presentation.
  if (status === EcoTokenStatus.DRAFT) {
    if (hasTrust) {
      return { label: "Rejected", color: "warning.main", showPulse: true, pulseWarning: true };
    }

    return { label: "Draft", color: "text.primary", showPulse: false };
  }

  // Used in trust/admin tables where approval may still be shown as an explicit state.
  if (status === EcoTokenStatus.APPROVED) {
    const label = approvedLabel ?? (role === "guardian" ? "Approved" : "Verified");
    return { label, color: "text.primary", showPulse: false };
  }

  // Used as generic declined state fallback in trust/admin pages.
  if (status === EcoTokenStatus.REJECTED || status === EcoTokenStatus.VERIFICATION_FAILED) {
    return { label: "Rejected", color: "warning.main", showPulse: false };
  }

  return null;
};
